import * as tslib_1 from "tslib";
import { BitmapImage2D } from "@awayjs/graphics";
import { ContextGLProfile, ContextMode, ContextGLClearMask } from "@awayjs/stage";
import { RTTBufferManager } from "./managers/RTTBufferManager";
import { DefaultMaterialGroup } from "./materials/DefaultMaterialGroup";
import { DepthRenderer } from "./DepthRenderer";
import { DistanceRenderer } from "./DistanceRenderer";
import { Filter3DRenderer } from "./Filter3DRenderer";
import { RendererBase } from "./RendererBase";
/**
 * The DefaultRenderer class provides the default rendering method. It renders the scene graph objects using the
 * materials assigned to them.
 *
 * @class away.render.DefaultRenderer
 */
var DefaultRenderer = (function (_super) {
    tslib_1.__extends(DefaultRenderer, _super);
    /**
     * Creates a new DefaultRenderer object.
     *
     * @param antiAlias The amount of anti-aliasing to use.
     * @param renderMode The render mode to use.
     */
    function DefaultRenderer(stage, forceSoftware, profile, mode) {
        if (stage === void 0) { stage = null; }
        if (forceSoftware === void 0) { forceSoftware = false; }
        if (profile === void 0) { profile = ContextGLProfile.BASELINE; }
        if (mode === void 0) { mode = ContextMode.AUTO; }
        var _this = _super.call(this, stage, forceSoftware, profile, mode) || this;
        _this._antiAlias = 0;
        _this._directionalLights = new Array();
        _this._pointLights = new Array();
        _this._lightProbes = new Array();
        if (stage)
            _this.shareContext = true;
        _this._materialGroup = new DefaultMaterialGroup(_this._pStage);
        _this._pRttBufferManager = RTTBufferManager.getInstance(_this._pStage);
        _this._depthRenderer = new DepthRenderer(_this._pStage);
        _this._distanceRenderer = new DistanceRenderer(_this._pStage);
        if (_this._width == 0)
            _this.width = window.innerWidth;
        else
            _this._pRttBufferManager.viewWidth = _this._width;
        if (_this._height == 0)
            _this.height = window.innerHeight;
        else
            _this._pRttBufferManager.viewHeight = _this._height;
        return _this;
    }
    Object.defineProperty(DefaultRenderer.prototype, "distanceRenderer", {
        get: function () {
            return this._distanceRenderer;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DefaultRenderer.prototype, "depthRenderer", {
        get: function () {
            return this._depthRenderer;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DefaultRenderer.prototype, "antiAlias", {
        get: function () {
            return this._antiAlias;
        },
        set: function (value) {
            if (this._antiAlias == value)
                return;
            this._antiAlias = value;
            this._pBackBufferInvalid = true;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DefaultRenderer.prototype, "depthPrepass", {
        /**
         *
         */
        get: function () {
            return this._depthPrepass;
        },
        set: function (value) {
            this._depthPrepass = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DefaultRenderer.prototype, "filters3d", {
        /**
         *
         * @returns {*}
         */
        get: function () {
            return this._pFilter3DRenderer ? this._pFilter3DRenderer.filters : null;
        },
        set: function (value) {
            if (value && value.length == 0)
                value = null;
            if (this._pFilter3DRenderer && !value) {
                this._pFilter3DRenderer.dispose();
                this._pFilter3DRenderer = null;
            }
            else if (!this._pFilter3DRenderer && value) {
                this._pFilter3DRenderer = new Filter3DRenderer(this._pStage);
                this._pFilter3DRenderer.filters = value;
            }
            if (this._pFilter3DRenderer) {
                this._pFilter3DRenderer.filters = value;
                this._pRequireDepthRender = this._pFilter3DRenderer.requireDepthRender;
            }
            else {
                this._pRequireDepthRender = false;
                if (this._pDepthRender) {
                    this._pDepthRender.dispose();
                    this._pDepthRender = null;
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     *
     */
    DefaultRenderer.prototype.enterNode = function (node) {
        var enter = _super.prototype.enterNode.call(this, node);
        if (enter && node.debugVisible)
            node.renderBounds(this);
        return enter;
    };
    DefaultRenderer.prototype.render = function (view) {
        _super.prototype.render.call(this, view);
        if (!this._pStage.recoverFromDisposal()) {
            this._pBackBufferInvalid = true;
            return;
        }
        if (this._pBackBufferInvalid)
            this.pUpdateBackBuffer();
        if (this.shareContext && this._pContext)
            this._pContext.clear(0, 0, 0, 1, 1, 0, ContextGLClearMask.DEPTH);
        if (this._pFilter3DRenderer) {
            this.textureRatioX = this._pRttBufferManager.textureRatioX;
            this.textureRatioY = this._pRttBufferManager.textureRatioY;
        }
        else {
            this.textureRatioX = 1;
            this.textureRatioY = 1;
        }
        if (this._pRequireDepthRender)
            this.pRenderSceneDepthToTexture(view);
        if (this._depthPrepass)
            this.pRenderDepthPrepass(view);
        //reset lights
        this._directionalLights.length = 0;
        this._pointLights.length = 0;
        this._lightProbes.length = 0;
        if (this._pFilter3DRenderer && this._pContext) {
            this._iRender(view.camera.projection, view, this._pFilter3DRenderer.getMainInputTexture(this._pStage), this._pFilter3DRenderer.renderToTextureRect);
            this._pFilter3DRenderer.render(this._pStage, view.camera, this._pDepthRender);
        }
        else {
            if (this.shareContext)
                this._iRender(view.camera.projection, view, null, this._pScissorRect);
            else
                this._iRender(view.camera.projection, view);
        }
        if (!this.shareContext && this._pContext)
            this._pContext.present();
        // register that a view has been rendered
        this._pStage.bufferClear = false;
    };
    DefaultRenderer.prototype.pExecuteRender = function (projection, view, target, scissorRect, surfaceSelector) {
        if (target === void 0) { target = null; }
        if (scissorRect === void 0) { scissorRect = null; }
        if (surfaceSelector === void 0) { surfaceSelector = 0; }
        this.updateLights(projection, view);
        _super.prototype.pExecuteRender.call(this, projection, view, target, scissorRect, surfaceSelector);
    };
    DefaultRenderer.prototype.updateLights = function (projection, view) {
        var len, i;
        var light;
        var shadowMapper;
        len = this._directionalLights.length;
        for (i = 0; i < len; ++i) {
            light = this._directionalLights[i];
            shadowMapper = light.shadowMapper;
            if (light.shadowsEnabled && (shadowMapper.autoUpdateShadows || shadowMapper._iShadowsInvalid))
                shadowMapper.iRenderDepthMap(view, this._depthRenderer);
        }
        len = this._pointLights.length;
        for (i = 0; i < len; ++i) {
            light = this._pointLights[i];
            shadowMapper = light.shadowMapper;
            if (light.shadowsEnabled && (shadowMapper.autoUpdateShadows || shadowMapper._iShadowsInvalid))
                shadowMapper.iRenderDepthMap(view, this._distanceRenderer);
        }
    };
    /**
     *
     * @param entity
     */
    DefaultRenderer.prototype.applyDirectionalLight = function (entity) {
        this._directionalLights.push(entity);
    };
    /**
     *
     * @param entity
     */
    DefaultRenderer.prototype.applyLightProbe = function (entity) {
        this._lightProbes.push(entity);
    };
    /**
     *
     * @param entity
     */
    DefaultRenderer.prototype.applyPointLight = function (entity) {
        this._pointLights.push(entity);
    };
    DefaultRenderer.prototype.dispose = function () {
        if (!this.shareContext)
            this._pStage.dispose();
        this._pRttBufferManager.dispose();
        this._pRttBufferManager = null;
        this._depthRenderer.dispose();
        this._distanceRenderer.dispose();
        this._depthRenderer = null;
        this._distanceRenderer = null;
        this._pDepthRender = null;
        _super.prototype.dispose.call(this);
    };
    /**
     *
     */
    DefaultRenderer.prototype.pRenderDepthPrepass = function (view) {
        this._depthRenderer.disableColor = true;
        if (this._pFilter3DRenderer) {
            this._depthRenderer.textureRatioX = this._pRttBufferManager.textureRatioX;
            this._depthRenderer.textureRatioY = this._pRttBufferManager.textureRatioY;
            this._depthRenderer._iRender(view.camera.projection, view, this._pFilter3DRenderer.getMainInputTexture(this._pStage), this._pRttBufferManager.renderToTextureRect);
        }
        else {
            this._depthRenderer.textureRatioX = 1;
            this._depthRenderer.textureRatioY = 1;
            this._depthRenderer._iRender(view.camera.projection, view);
        }
        this._depthRenderer.disableColor = false;
    };
    /**
     *
     */
    DefaultRenderer.prototype.pRenderSceneDepthToTexture = function (view) {
        if (this._pDepthTextureInvalid || !this._pDepthRender)
            this.initDepthTexture(this._pStage.context);
        this._depthRenderer.textureRatioX = this._pRttBufferManager.textureRatioX;
        this._depthRenderer.textureRatioY = this._pRttBufferManager.textureRatioY;
        this._depthRenderer._iRender(view.camera.projection, view, this._pDepthRender);
    };
    /**
     * Updates the backbuffer dimensions.
     */
    DefaultRenderer.prototype.pUpdateBackBuffer = function () {
        // No reason trying to configure back buffer if there is no context available.
        // Doing this anyway (and relying on _stage to cache width/height for
        // context does get available) means usesSoftwareRendering won't be reliable.
        if (this._pStage.context && !this.shareContext) {
            if (this._width && this._height) {
                this._pStage.configureBackBuffer(this._width, this._height, this._antiAlias, true);
                this._pBackBufferInvalid = false;
            }
        }
    };
    /**
     *
     */
    DefaultRenderer.prototype.initDepthTexture = function (context) {
        this._pDepthTextureInvalid = false;
        if (this._pDepthRender)
            this._pDepthRender.dispose();
        this._pDepthRender = new BitmapImage2D(this._pRttBufferManager.textureWidth, this._pRttBufferManager.textureHeight);
    };
    return DefaultRenderer;
}(RendererBase));
export { DefaultRenderer };



// WEBPACK FOOTER //
// ../dist/lib/DefaultRenderer.js